import React, { Fragment, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Callout, Body } from '../../../../components/Typography';
import Select from '../../../../components/Select';
import { Link } from 'gatsby';
import LocationCustomFormatLabel from './LocationCustomLabel';
import OrderFutureSchedule from './OrderFutureSchedule';
import ConfirmButton from './ConfirmButton';
import UnavailableProducts from './UnavailableProducts';
import {
  renderConfirmButtonText,
  isConfirmButtonDisabled,
  isLocationAcceptingOrders,
  isScheduleRadioDisabledCalculated,
} from '../utils/helper';
import appConfig from '../../../../../app-config';
import { isGuestOrderInfoClient } from '../../../../clientCustomizations';

const Wrapper = styled.div`
  display: grid;
  grid-gap: 24px;
`;

const SelectTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AddNewButton = styled(Link)`
  margin: 0;
  letter-spacing: 0px;
  text-decoration: none;
`;

const CalloutWrapper = styled(Callout)`
  &:hover {
    color: ${(props) => props.theme.clickableTextColorHover};
  }
`;

const PickUp = ({
  interPickUpDetails,
  locations,
  updateInterPickUpDetails,
  availableTimes,
  interSelectedDateTime,
  interOrderType,
  updateInterSelectedDateTime,
  getTimeIntervalsDate,
  confirmPickUpDetails,
  unavailableProducts,
  isRequesting,
  requestingGetLocations,
  isTimeNoLongerValid,
  resetIsTimeNoLongerValid,
  selectedDateTime,
  currentLocation,
  handleConfirmPickupButton,
  specialHours,
  orderPacingData,
  refine,
  selectedLocation,
  orderSettingModalIsVisible,
  updateSelectedLocation,
  cartErrorMessage,
  deepLinkParameters,
  checkUnavailableProductForPickUp,
  cartReducer,
  getMenu,
  emptyCart,
  storeId,
  showConfirmButton,
  setShowConfirmButton,
}) => {
  const [isOnlyOneLocationAvailable, setIsOnlyOneLocationAvailable] = useState(
    false
  );

  const isAsapRadioDisabled =
    selectedLocation &&
    selectedLocation?.settings &&
    !selectedLocation?.settings.online;

  const maxFutureOrderDate =
    selectedLocation &&
    selectedLocation?.settings &&
    selectedLocation?.settings.max_future_order_date;

  const orderEndTime =
    availableTimes &&
    availableTimes.length &&
    (availableTimes.length > 1
      ? availableTimes[1].order_end_time
      : availableTimes[0].order_end_time);

  const isScheduleRadioDisabled = !(
    availableTimes &&
    maxFutureOrderDate &&
    orderEndTime &&
    specialHours &&
    selectedLocation &&
    !isScheduleRadioDisabledCalculated(
      availableTimes,
      maxFutureOrderDate,
      orderEndTime,
      specialHours,
      selectedLocation?.settings.online
    )
  );

  const [location, setLocation] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const updateConfirmButtonDisplay = useCallback(() => {
    // if (selectedLocation && availableTimes)
    if (
      !selectedLocation?.is_order_ahead_enabled ||
      !selectedLocation?.accepting_orders
    ) {
      setShowConfirmButton(false);
    } else {
      setShowConfirmButton(true);
    }
  }, [selectedLocation, availableTimes]);

  useEffect(() => {
    updateConfirmButtonDisplay();
  }, [updateConfirmButtonDisplay]);

  useEffect(() => {
    if (selectedLocation && !deepLinkParameters?.id) {
      const pickedLocation = locations.filter(
        (location) => location.name === selectedLocation
      );

      if (pickedLocation.length > 0) {
        setLocation(pickedLocation[0]);
      } else {
        setLocation(null);
      }
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (!orderSettingModalIsVisible) {
      setLocation(null);
    }
  }, [orderSettingModalIsVisible]);

  useEffect(() => {
    if (deepLinkParameters?.id && location) {
      let selectedDateTimeModified = {
        ...interSelectedDateTime,
        radioValue: { name: 'As soon as possible', value: 'asap' },
      };

      updateInterPickUpDetails(location, new Date().toString());
      updateInterSelectedDateTime(selectedDateTimeModified);
      if (interPickUpDetails) {
        handleConfirmPickupButton();
        emptySearchBox();
      }
    }
  }, [deepLinkParameters, location, interPickUpDetails]);

  const emptySearchBox = () => {
    if (appConfig.isGlobalSearchEnabled) {
      refine('');
    }
  };
  const checkProductAvailibility = useCallback(() => {
    if (
      ((selectedLocation &&
        interSelectedDateTime.radioValue.value === 'asap') ||
        (interSelectedDateTime.radioValue.value === 'schedule' &&
          interSelectedDateTime.dateTime.time.value)) &&
      interOrderType === 'pickup'
    ) {
      if (interPickUpDetails?.id !== selectedLocation?.id)
        checkUnavailableProductForPickUp(selectedLocation);
    }
  }, [interSelectedDateTime, selectedLocation]);
  useEffect(() => {
    checkProductAvailibility();
  }, [checkProductAvailibility]);

  useEffect(() => {
    if (storeId && !selectedLocation && orderSettingModalIsVisible) {
      updateSelectedLocation(interPickUpDetails);
    }
  }, [storeId, orderSettingModalIsVisible]);

  useEffect(() => {
    if (locations?.length === 1 && !interPickUpDetails?.id) {
      updateSelectedLocation(locations[0]);
      setIsOnlyOneLocationAvailable(true);
    }
  }, [locations?.length, interPickUpDetails?.id, updateSelectedLocation]);

  return (
    <Wrapper>
      <SelectTitleWrapper>
        <Body style={{ fontWeight: 'bold' }} textTransform='normal'>
          {appConfig?.pickingUpFrom ?? 'Picking up from:'}
        </Body>
        <AddNewButton to='/locations' onClick={() => {}}>
          <CalloutWrapper>View Map</CalloutWrapper>
        </AddNewButton>
      </SelectTitleWrapper>
      <div>
        <form autocomplete='off'>
          <Select
            value={
              selectedLocation && !selectedLocation?.deliveryBusiness
                ? selectedLocation
                : null
            }
            onChange={(location) => updateSelectedLocation(location)}
            options={locations}
            getOptionLabel={(location) => location.name}
            getOptionValue={(location) => location}
            onMenuOpen={() => {
              setMenuOpen(true);
            }}
            onMenuClose={() => {
              setMenuOpen(false);
            }}
            formatOptionLabel={(location, { context }) => {
              if (
                !appConfig.isPickupAddressSelectionDisabled &&
                context === 'menu'
              ) {
                return (
                  <LocationCustomFormatLabel
                    location={location}
                    coords={currentLocation}
                    interPickUpDetails={interPickUpDetails}
                    specialHours={specialHours}
                    displayAddress={menuOpen}
                    isLastLocation={
                      location.id === locations[locations.length - 1]?.id
                    }
                  />
                );
              }
              return (
                <LocationCustomFormatLabel
                  location={location}
                  coords={currentLocation}
                  interPickUpDetails={interPickUpDetails}
                  specialHours={specialHours}
                  displayAddress={false}
                />
              );
            }}
            placeholder='Select a location...'
            maxMenuHeight={200}
            isLocation={true}
            isDisabled={
              isRequesting ||
              requestingGetLocations ||
              isOnlyOneLocationAvailable
            }
            coords={currentLocation}
            nearestLocation={selectedLocation}
            specialHours={specialHours}
            interOrderType={interOrderType}
            triggerOnChange
          />
        </form>
      </div>
      <Fragment>
        {selectedLocation && (
          <OrderFutureSchedule
            availableTimes={availableTimes}
            selectedLocation={selectedLocation}
            updateInterPickUpDetails={updateInterPickUpDetails}
            interOrderType={interOrderType}
            interSelectedDateTime={interSelectedDateTime}
            updateInterSelectedDateTime={updateInterSelectedDateTime}
            getTimeIntervalsDate={getTimeIntervalsDate}
            confirmPickUpDetails={confirmPickUpDetails}
            isTimeNoLongerValid={isTimeNoLongerValid}
            resetIsTimeNoLongerValid={resetIsTimeNoLongerValid}
            selectedDateTime={selectedDateTime}
            is_future_order_enabled={
              selectedLocation?.settings.is_future_order_enabled
            }
            is_asap_order_enabled={
              selectedLocation?.settings.is_asap_order_enabled
            }
            maxFutureOrderDate={
              selectedLocation
                ? selectedLocation?.settings.max_future_order_date
                : null
            }
            isLocationOrDelivBusinesssAcceptingOrders={
              isLocationAcceptingOrders(
                selectedLocation?.accepting_orders,
                specialHours
              )
              // interPickUpDetails.accepting_orders
            }
            specialHours={specialHours}
            orderPacingData={orderPacingData}
            orderMessage={appConfig.orderMessagePickup}
            isOnline={selectedLocation?.settings.online}
            emptySearchBox={() => emptySearchBox()}
            deepLinkParameters={deepLinkParameters}
            onSeeMenu={handleConfirmPickupButton}
            cartReducer={cartReducer}
            emptyCart={emptyCart}
            updateSelectedLocation={updateSelectedLocation}
            showConfirmButton={showConfirmButton}
            setShowConfirmButton={setShowConfirmButton}
          />
        )}
        {cartErrorMessage && cartErrorMessage.length ? (
          <UnavailableProducts
            unavailableProducts={unavailableProducts}
            cartErrorMessage={cartErrorMessage}
          />
        ) : null}
        {showConfirmButton && (
          <ConfirmButton
            text={renderConfirmButtonText(
              selectedLocation,
              interSelectedDateTime,
              'pickup'
            )}
            onClick={() => {
              handleConfirmPickupButton(selectedLocation);
            }}
            disabled={isConfirmButtonDisabled(
              interSelectedDateTime,
              selectedLocation,
              'pickup'
            )}
          />
        )}
      </Fragment>
    </Wrapper>
  );
};

export default PickUp;
